import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClientAssociationGroup,
  CreateClientAssociationGroupParams,
  CreateClientAssociationGroupResponse,
  DeleteClientAssociationGroupParams,
  DeleteClientAssociationGroupResponse,
  GetClientAssociationGroupsResponse,
  UpdateClientAssociationGroupParams,
  UpdateClientAssociationGroupResponse
} from '@blisspointmedia/bpm-types/dist/ClientAssociations';
import { awaitJSON, UsersLambdaFetch } from '../../utils/fetch-utils';

const QUERY_KEY = ['clientAssociationGroups'] as const;

const fetchGroups = async (): Promise<ClientAssociationGroup[]> => {
  const res = await UsersLambdaFetch('/getClientAssociationGroups');
  const data = await awaitJSON<GetClientAssociationGroupsResponse>(res);
  return data.groups;
};

const createGroup = async (
  body: CreateClientAssociationGroupParams
): Promise<ClientAssociationGroup> => {
  const res = await UsersLambdaFetch('/createClientAssociationGroup', {
    method: 'POST',
    body
  });
  const data = await awaitJSON<CreateClientAssociationGroupResponse>(res);
  return data.group;
};

const updateGroup = async (
  body: UpdateClientAssociationGroupParams
): Promise<ClientAssociationGroup> => {
  const res = await UsersLambdaFetch('/updateClientAssociationGroup', {
    method: 'POST',
    body
  });
  const data = await awaitJSON<UpdateClientAssociationGroupResponse>(res);
  return data.group;
};

const deleteGroup = async (
  body: DeleteClientAssociationGroupParams
): Promise<DeleteClientAssociationGroupResponse> => {
  const res = await UsersLambdaFetch('/deleteClientAssociationGroup', {
    method: 'POST',
    body
  });
  return awaitJSON<DeleteClientAssociationGroupResponse>(res);
};

export const useClientAssociationGroups = () => {
  const queryClient = useQueryClient();
  const query = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: QUERY_KEY,
    queryFn: fetchGroups
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const createMutation = useMutation({
    mutationFn: createGroup,
    onSuccess: invalidate
  });

  const updateMutation = useMutation({
    mutationFn: updateGroup,
    onSuccess: invalidate
  });

  const deleteMutation = useMutation({
    mutationFn: deleteGroup,
    onSuccess: invalidate
  });

  return {
    groups: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createGroup: createMutation.mutateAsync,
    updateGroup: updateMutation.mutateAsync,
    deleteGroup: deleteMutation.mutateAsync
  };
};
